import { TFunction } from 'i18next'
import { z } from 'zod'
import {
  ACTIVE_POWER_FUNCTION_MODEL,
  DeadBandThresholdField,
  FrequencyProtection,
  PHASE_SHIFT,
  REACTIVE_POWER_FUNCTION_MODEL,
  VoltageProtection,
} from './common'
import {
  ActivePowerSettingsModel,
  DeadBandThresholdSettingsModel,
  DynamicGridRegenerationSettingsModel,
  FrequencyMonitoringSettingsModel,
  LfsmOSettingsModel,
  LfsmUSettingsModel,
  MainVoltageLimitsSettingsModel,
  ReactivePowerSettingsModel,
  RestartConditionsSettingsModel,
} from './grid-and-system-protection.model'

// region schema generators
export const getFrequencyMonitoringSettingsSchema = ({
  frequencyGainProtection,
  frequencyDropProtection,
}: FrequencyMonitoringSettingsModel): z.ZodType<FrequencyMonitoringSettingsForm> =>
  z.object({
    frequencyGainProtection: z.object({
      frequency: numWithBounds(frequencyGainProtection.frequency.min, frequencyGainProtection.frequency.max),
      timeSeconds: numWithBounds(frequencyGainProtection.timeSeconds.min, frequencyGainProtection.timeSeconds.max),
    }),
    frequencyDropProtection: z.object({
      frequency: numWithBounds(frequencyDropProtection.frequency.min, frequencyDropProtection.frequency.max),
      timeSeconds: numWithBounds(frequencyDropProtection.timeSeconds.min, frequencyDropProtection.timeSeconds.max),
    }),
  })

export const getLfsmOSettingsSchema = ({
  thresholdFrequency,
  gradient,
  returnGradient,
  statics,
  activePowerReference,
}: LfsmOSettingsModel): z.ZodType<LfsmOSettingsForm> =>
  z.object({
    thresholdFrequency: numWithBounds(thresholdFrequency.min, thresholdFrequency.max),
    gradient: numWithBounds(gradient.min, gradient.max),
    returnGradient: numWithBounds(returnGradient.min, returnGradient.max),
    statics: numWithBounds(statics.min, statics.max),
    activePowerReference: activePowerReference ? numWithBounds(activePowerReference.min, activePowerReference.max) : z.null(),
  })

export const getLfsmUSettingsSchema = ({
  thresholdVoltage,
  gradient,
  returnGradient,
  statics,
  activePowerReference,
}: LfsmUSettingsModel): z.ZodType<LfsmUSettingsForm> =>
  z.object({
    thresholdVoltage: numWithBounds(thresholdVoltage.min, thresholdVoltage.max),
    gradient: numWithBounds(gradient.min, gradient.max),
    returnGradient: numWithBounds(returnGradient.min, returnGradient.max),
    statics: numWithBounds(statics.min, statics.max),
    activePowerReference: activePowerReference ? numWithBounds(activePowerReference.min, activePowerReference.max) : z.null(),
  })

export const getMainVoltageLimitsSettingsSchema = ({
  voltageGainProtection,
  voltageDropProtection,
}: MainVoltageLimitsSettingsModel): z.ZodType<MainVoltageLimitsSettingsForm> =>
  z.object({
    voltageGainProtection: z.object({
      voltage: numWithBounds(voltageGainProtection.voltage.min, voltageGainProtection.voltage.max),
      timeSeconds: numWithBounds(voltageGainProtection.timeSeconds.min, voltageGainProtection.timeSeconds.max),
      largerVoltage: numWithBounds(voltageGainProtection.largerVoltage.min, voltageGainProtection.largerVoltage.max),
      largerTimeSeconds: numWithBounds(voltageGainProtection.largerTimeSeconds.min, voltageGainProtection.largerTimeSeconds.max),
    }),
    voltageDropProtection: z.object({
      voltage: numWithBounds(voltageDropProtection.voltage.min, voltageDropProtection.voltage.max),
      timeSeconds: numWithBounds(voltageDropProtection.timeSeconds.min, voltageDropProtection.timeSeconds.max),
      largerVoltage: numWithBounds(voltageDropProtection.largerVoltage.min, voltageDropProtection.largerVoltage.max),
      largerTimeSeconds: numWithBounds(voltageDropProtection.largerTimeSeconds.min, voltageDropProtection.largerTimeSeconds.max),
    }),
  })

export const getRestartConditionsSettingsSchema = ({
  restartConditionsTimeSettings,
  restartConditionsGradientSettings,
  restartConditionsVoltageSettings,
  restartConditionsFrequencySettings,
}: RestartConditionsSettingsModel): z.ZodType<RestartConditionsSettingsForm> =>
  z.object({
    restartConditionsTimeSettings: z
      .object({
        delayStandard: numWithBounds(restartConditionsTimeSettings?.delayStandard.min, restartConditionsTimeSettings?.delayStandard.max),
        delayFault: numWithBounds(restartConditionsTimeSettings?.delayFault.min, restartConditionsTimeSettings?.delayFault.max),
      })
      .optional(),
    restartConditionsGradientSettings: z
      .object({
        gradientStandard: numWithBounds(
          restartConditionsGradientSettings?.gradientStandard.min,
          restartConditionsGradientSettings?.gradientStandard.max
        ),
        gradientFault: numWithBounds(restartConditionsGradientSettings?.gradientFault.min, restartConditionsGradientSettings?.gradientFault.max),
      })
      .optional(),
    restartConditionsVoltageSettings: z
      .object({
        lowerVoltageThresholdStandard: numWithBounds(
          restartConditionsVoltageSettings?.lowerVoltageThresholdStandard.min,
          restartConditionsVoltageSettings?.lowerVoltageThresholdStandard.max
        ),
        lowerVoltageThresholdFault: numWithBounds(
          restartConditionsVoltageSettings?.lowerVoltageThresholdFault.min,
          restartConditionsVoltageSettings?.lowerVoltageThresholdFault.max
        ),
        upperVoltageThresholdStandard: numWithBounds(
          restartConditionsVoltageSettings?.upperVoltageThresholdStandard.min,
          restartConditionsVoltageSettings?.upperVoltageThresholdStandard.max
        ),
        upperVoltageThresholdFault: numWithBounds(
          restartConditionsVoltageSettings?.upperVoltageThresholdFault.min,
          restartConditionsVoltageSettings?.upperVoltageThresholdFault.max
        ),
      })
      .optional(),
    restartConditionsFrequencySettings: z
      .object({
        lowerFrequencyThresholdStandard: numWithBounds(
          restartConditionsFrequencySettings?.lowerFrequencyThresholdStandard.min,
          restartConditionsFrequencySettings?.lowerFrequencyThresholdStandard.max
        ),
        lowerFrequencyThresholdFault: numWithBounds(
          restartConditionsFrequencySettings?.lowerFrequencyThresholdFault.min,
          restartConditionsFrequencySettings?.lowerFrequencyThresholdFault.max
        ),
        upperFrequencyThresholdStandard: numWithBounds(
          restartConditionsFrequencySettings?.upperFrequencyThresholdStandard.min,
          restartConditionsFrequencySettings?.upperFrequencyThresholdStandard.max
        ),
        upperFrequencyThresholdFault: numWithBounds(
          restartConditionsFrequencySettings?.upperFrequencyThresholdFault.min,
          restartConditionsFrequencySettings?.upperFrequencyThresholdFault.max
        ),
      })
      .optional(),
  })

export const getDynamicGridRegenerationSettingsSchema = ({
  kFactor,
}: DynamicGridRegenerationSettingsModel): z.ZodType<DynamicGridRegenerationSettingsForm> =>
  z.object({
    dynamicReactiveCurrentControl: z.boolean(),
    kFactor: z.object({
      positiveSequenceSystem: numWithBounds(kFactor.positiveSequenceSystem.min, kFactor.positiveSequenceSystem.max),
      negativeSequenceSystem: numWithBounds(kFactor.negativeSequenceSystem.min, kFactor.negativeSequenceSystem.max),
    }),
  })

export const getDeadBandThresholdSettingsSchema = ({
  reactiveCurrentControl,
  zeroCurrentMode,
}: DeadBandThresholdSettingsModel): z.ZodType<DeadBandThresholdSettingsForm> =>
  z.object({
    reactiveCurrentControl: z.object({
      lower: numWithBounds(reactiveCurrentControl.lower.min, reactiveCurrentControl.upper.max),
      upper: numWithBounds(reactiveCurrentControl.lower.min, reactiveCurrentControl.upper.max),
    }),
    zeroCurrentMode: z.object({
      lower: numWithBounds(zeroCurrentMode.lower.min, zeroCurrentMode.upper.max),
      upper: numWithBounds(zeroCurrentMode.lower.min, zeroCurrentMode.upper.max),
    }),
  })

export const getReactivePowerSettingsSchema = (
  { threeTau, fulcrums, minCosPhi }: ReactivePowerSettingsModel,
  t: TFunction
): z.ZodType<ReactivePowerSettingsForm> => {
  const fixedCosPhiItem = fulcrums.find(f => REACTIVE_POWER_FUNCTION_MODEL.fixCosPhi in f)?.[REACTIVE_POWER_FUNCTION_MODEL.fixCosPhi]
  const cosPhiItem = fulcrums.find(f => REACTIVE_POWER_FUNCTION_MODEL.cosPhi in f)
  const qUItem = fulcrums.find(f => REACTIVE_POWER_FUNCTION_MODEL.qU in f)
  const fixedQItem = fulcrums.find(f => REACTIVE_POWER_FUNCTION_MODEL.fixedQ in f)?.[REACTIVE_POWER_FUNCTION_MODEL.fixedQ]

  const cosPhiLimits = cosPhiItem?.[REACTIVE_POWER_FUNCTION_MODEL.cosPhi]?.limits || []
  const qULimits = qUItem?.[REACTIVE_POWER_FUNCTION_MODEL.qU]?.limits || []

  return z
    .object({
      selectedFunction: z.nativeEnum(REACTIVE_POWER_FUNCTION_MODEL),
      threeTau: numWithBounds(threeTau.min, threeTau.max),
      fulcrums: z.object({
        [REACTIVE_POWER_FUNCTION_MODEL.fixCosPhi]: z
          .array(z.tuple([z.null(), numWithBounds(fixedCosPhiItem?.value.min, fixedCosPhiItem?.value.max), z.nativeEnum(PHASE_SHIFT)]))
          .optional(),
        [REACTIVE_POWER_FUNCTION_MODEL.cosPhi]: z.array(z.tuple([z.number(), z.number(), z.nativeEnum(PHASE_SHIFT)])).optional(),
        [REACTIVE_POWER_FUNCTION_MODEL.qU]: z.array(z.tuple([z.number(), z.number(), z.nativeEnum(PHASE_SHIFT)])).optional(),
        [REACTIVE_POWER_FUNCTION_MODEL.fixedQ]: z
          .array(z.tuple([z.null(), numWithBounds(fixedQItem?.value.min, fixedQItem?.value.max), z.nativeEnum(PHASE_SHIFT)]))
          .optional(),
      }),
      minCosPhi: minCosPhi ? z.number() : z.undefined(),
    })
    .superRefine((data, ctx) => {
      // ? this superRefine is responsible for only validating the fulcrums that are selected by the selectedFunction dropdown
      const { selectedFunction, fulcrums } = data

      if (!fulcrums[selectedFunction] || !Array.isArray(fulcrums[selectedFunction])) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Selected fulcrum is required',
          path: ['fulcrums', selectedFunction],
        })
        return
      }

      if (selectedFunction === REACTIVE_POWER_FUNCTION_MODEL.cosPhi) {
        fulcrums[REACTIVE_POWER_FUNCTION_MODEL.cosPhi]?.forEach(([x, y], index) => {
          const limit = cosPhiLimits[index]
          if (!limit) return
          if (x < limit.x.min || x > limit.x.max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('validation.error-message.within-range', { min: limit.x.min, max: limit.x.max }),
              path: ['fulcrums', REACTIVE_POWER_FUNCTION_MODEL.cosPhi, index, 0],
            })
          }
          if (y < limit.y.min || y > limit.y.max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('validation.error-message.within-range', { min: limit.y.min, max: limit.y.max }),
              path: ['fulcrums', REACTIVE_POWER_FUNCTION_MODEL.cosPhi, index, 1],
            })
          }
        })
      }
      if (selectedFunction === REACTIVE_POWER_FUNCTION_MODEL.qU) {
        fulcrums[REACTIVE_POWER_FUNCTION_MODEL.qU]?.forEach(([x, y], index) => {
          const limit = qULimits[index]
          if (!limit) return
          if (x < limit.x.min || x > limit.x.max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('validation.error-message.within-range', { min: limit.x.min, max: limit.x.max }),
              path: ['fulcrums', REACTIVE_POWER_FUNCTION_MODEL.qU, index, 0],
            })
          }
          if (y < limit.y.min || y > limit.y.max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('validation.error-message.within-range', { min: limit.y.min, max: limit.y.max }),
              path: ['fulcrums', REACTIVE_POWER_FUNCTION_MODEL.qU, index, 1],
            })
          }
        })
      }
    })
}

export const getActivePowerSettingsSchema = ({ threeTau, fulcrums }: ActivePowerSettingsModel, t: TFunction): z.ZodType<ActivePowerSettingsForm> => {
  const typeAItem = fulcrums.find(f => ACTIVE_POWER_FUNCTION_MODEL.typeA in f)
  const typeBItem = fulcrums.find(f => ACTIVE_POWER_FUNCTION_MODEL.typeB in f)

  const typeALimits = typeAItem?.[ACTIVE_POWER_FUNCTION_MODEL.typeA]?.limits || []
  const typeBLimits = typeBItem?.[ACTIVE_POWER_FUNCTION_MODEL.typeB]?.limits || []

  return z.object({
    selectedFunction: z.nativeEnum(ACTIVE_POWER_FUNCTION_MODEL),
    threeTau: numWithBounds(threeTau.min, threeTau.max),
    fulcrums: z.object({
      [ACTIVE_POWER_FUNCTION_MODEL.typeA]: z.array(z.tuple([z.number(), z.number()])).superRefine((values, ctx) => {
        values.forEach(([x, y], index) => {
          const limit = typeALimits[index]
          if (!limit) return

          if (x < limit.x.min || x > limit.x.max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('validation.error-message.within-range', { min: limit.x.min, max: limit.x.max }),
              path: [index, 0],
            })
          }
          if (y < limit.y.min || y > limit.y.max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('validation.error-message.within-range', { min: limit.y.min, max: limit.y.max }),
              path: [index, 1],
            })
          }
        })
      }),
      [ACTIVE_POWER_FUNCTION_MODEL.typeB]: z.array(z.tuple([z.number(), z.number()])).superRefine((values, ctx) => {
        values.forEach(([x, y], index) => {
          const limit = typeBLimits[index]
          if (!limit) return

          if (x < limit.x.min || x > limit.x.max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('validation.error-message.within-range', { min: limit.x.min, max: limit.x.max }),
              path: [index, 0],
            })
          }
          if (y < limit.y.min || y > limit.y.max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('validation.error-message.within-range', { min: limit.y.min, max: limit.y.max }),
              path: [index, 1],
            })
          }
        })
      }),
    }),
  })
}

const numWithBounds = (min?: number | null, max?: number | null): z.ZodNullable<z.ZodNumber> => {
  let s = z.number()
  if (typeof min === 'number') s = s.min(min)
  if (typeof max === 'number') s = s.max(max)
  return s.nullable()
}

// region Types
export type GridAndSystemProtectionForm = {
  frequencyMonitoringSettings: FrequencyMonitoringSettingsForm
  lfsmOSettings: LfsmOSettingsForm
  lfsmUSettings: LfsmUSettingsForm
  mainVoltageLimitsSettings: MainVoltageLimitsSettingsForm
  restartConditionsTimeSettings?: RestartConditionsTimeForm
  restartConditionsGradientSettings?: RestartConditionsGradientForm
  restartConditionsVoltageSettings?: RestartConditionsVoltageForm
  restartConditionsFrequencySettings?: RestartConditionsFrequencyForm
  dynamicGridRegenerationSettings: DynamicGridRegenerationSettingsForm
  deadBandThresholdSettings: DeadBandThresholdSettingsForm
  reactivePowerSettings: Partial<ReactivePowerSettingsForm>
  activePowerSettings: ActivePowerSettingsForm
}

export type FrequencyMonitoringSettingsForm = {
  frequencyGainProtection: FrequencyProtection
  frequencyDropProtection: FrequencyProtection
}

export type LfsmOSettingsForm = {
  thresholdFrequency: number | null
  gradient: number | null
  returnGradient: number | null
  statics: number | null
  activePowerReference: number | null
}

export type LfsmUSettingsForm = {
  thresholdVoltage: number | null
  gradient: number | null
  returnGradient: number | null
  statics: number | null
  activePowerReference: number | null
}

export type MainVoltageLimitsSettingsForm = {
  voltageGainProtection: VoltageProtection
  voltageDropProtection: VoltageProtection
}

export type RestartConditionsSettingsForm = {
  restartConditionsTimeSettings?: RestartConditionsTimeForm
  restartConditionsGradientSettings?: RestartConditionsGradientForm
  restartConditionsVoltageSettings?: RestartConditionsVoltageForm
  restartConditionsFrequencySettings?: RestartConditionsFrequencyForm
}

export type RestartConditionsTimeForm = {
  delayStandard: number | null
  delayFault: number | null
}

export type RestartConditionsGradientForm = {
  gradientStandard: number | null
  gradientFault: number | null
}

export type RestartConditionsVoltageForm = {
  lowerVoltageThresholdStandard: number | null
  lowerVoltageThresholdFault: number | null
  upperVoltageThresholdStandard: number | null
  upperVoltageThresholdFault: number | null
}

export type RestartConditionsFrequencyForm = {
  lowerFrequencyThresholdStandard: number | null
  lowerFrequencyThresholdFault: number | null
  upperFrequencyThresholdStandard: number | null
  upperFrequencyThresholdFault: number | null
}

export type DynamicGridRegenerationSettingsForm = {
  dynamicReactiveCurrentControl: boolean
  kFactor: {
    positiveSequenceSystem: number | null
    negativeSequenceSystem: number | null
  }
}

export type DeadBandThresholdSettingsForm = {
  reactiveCurrentControl: DeadBandThresholdField
  zeroCurrentMode: DeadBandThresholdField
}

export type ReactivePowerSettingsForm = {
  selectedFunction: REACTIVE_POWER_FUNCTION_MODEL
  threeTau: number | null
  fulcrums: {
    [REACTIVE_POWER_FUNCTION_MODEL.fixCosPhi]?: [null, number | null, PHASE_SHIFT][]
    [REACTIVE_POWER_FUNCTION_MODEL.cosPhi]?: [number | null, number | null, PHASE_SHIFT][]
    [REACTIVE_POWER_FUNCTION_MODEL.qU]?: [number | null, number | null, PHASE_SHIFT][]
    [REACTIVE_POWER_FUNCTION_MODEL.fixedQ]?: [null, number | null, PHASE_SHIFT][]
  }
  minCosPhi?: number | null
}

export type ActivePowerSettingsForm = {
  selectedFunction: ACTIVE_POWER_FUNCTION_MODEL
  threeTau: number | null
  fulcrums: {
    [ACTIVE_POWER_FUNCTION_MODEL.disabled]?: []
    [ACTIVE_POWER_FUNCTION_MODEL.typeA]: [number | null, number | null][]
    [ACTIVE_POWER_FUNCTION_MODEL.typeB]: [number | null, number | null][]
  }
}
